import { useEffect, useMemo, useRef, useState } from 'react'
import { api } from '../lib/api'
import { audioStore } from '../lib/blobStore'
import { audioCacheKey, downloadAudioFully } from '../lib/audioDownload'
import { continuousSelectedRange, isContiguous, PAUSE_MS, segnoAtTime, selectedQueue } from '../lib/segmentQueue'
import { clampSeekTime } from '../lib/playbackSeek'
import { DEFAULT_PRACTICE_PLAYBACK_RATE, type PracticePlaybackRate } from '../lib/playbackRate'
import { adjacentSegno, firstSegnoOrder, orderedSegnos, type SegnoMoveDirection } from '../lib/segnoNavigation'
import { useWakeLock } from '../hooks/useWakeLock'
import { usePlaybackVolume } from '../hooks/usePlaybackVolume'
import { usePracticeTelemetry } from '../hooks/usePracticeTelemetry'
import type { AppStatus, Playback, PublicAlbum, Segno, Song } from '../types'
import { PdfViewer, type PdfViewerHandle } from './PdfViewer'
import { YouTubePractice, type YouTubePracticeHandle } from './YouTubePractice'
import { PlaybackSeekControls } from './PlaybackSeekControls'
import { PlaybackSpeedControl } from './PlaybackSpeedControl'
import { PracticePreferenceControls } from './PlaybackPreferenceControls'
import { NonPersonalizedAdSlot } from './NonPersonalizedAdSlot'
import { userMessageForError } from '../lib/userError'
import { useI18n } from '../i18n/useI18n'

const GENERAL_PLAYBACK_HINT_DURATION_MS = 4200

function formatPracticeAvailableDate(value: string, locale: string): string {
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return value
  return date.toLocaleDateString(locale)
}

interface Props {
  shareId?: string
  ownerSongId?: number
  onBack: () => void
  album?: PublicAlbum
  onAlbumSongSelect?: (shareId: string) => void
}

export function PracticePlayer({ shareId, ownerSongId, onBack, album, onAlbumSongSelect }: Props) {
  const { locale, t } = useI18n()
  const ownerPractice = ownerSongId != null
  const [song, setSong] = useState<Song | null>(null)
  const [playback, setPlayback] = useState<Playback | null>(null)
  const [sourceUrl, setSourceUrl] = useState<string | null>(null)
  const [selected, setSelected] = useState<Set<number>>(new Set())
  const [activeOrder, setActiveOrder] = useState<number | null>(null)
  const [looping, setLooping] = useState(false)
  const [nativeAudioPlaying, setNativeAudioPlaying] = useState(false)
  const [playbackRate, setPlaybackRate] = useState<PracticePlaybackRate>(DEFAULT_PRACTICE_PLAYBACK_RATE)
  // YouTube IFrame의 실제 PLAYING 상태. looping은 사용자가 요청한 반복 상태일 뿐
  // 실제 영상 재생 성공 여부와 다를 수 있으므로 telemetry에는 사용하지 않습니다.
  const [youtubePlaying, setYoutubePlaying] = useState(false)
  const [continuousPlayback, setContinuousPlayback] = useState(true)
  const [tab, setTab] = useState<'lyrics' | 'pdf'>('lyrics')
  const [pdfToolsCompact, setPdfToolsCompact] = useState(true)
  const [pdfHasDocument, setPdfHasDocument] = useState(false)
  const [message, setMessage] = useState(ownerPractice ? t('practice.message.loadingOwner') : t('practice.message.loadingShared'))
  const [cacheState, setCacheState] = useState<'checking' | 'waiting' | 'downloading' | 'ready' | 'error'>('checking')
  const [downloadProgress, setDownloadProgress] = useState<number | null>(null)
  const [reportOpen, setReportOpen] = useState(false)
  const [copyrightPolicyUrl, setCopyrightPolicyUrl] = useState<string | null>(null)
  const [appStatus, setAppStatus] = useState<AppStatus | null>(null)
  const [albumPickerOpen, setAlbumPickerOpen] = useState(false)
  const [practiceFullscreen, setPracticeFullscreen] = useState(false)
  const [generalPlaybackHintVisible, setGeneralPlaybackHintVisible] = useState(false)
  const practicePageRef = useRef<HTMLElement | null>(null)
  const nativeFullscreenEnteredRef = useRef(false)
  const audioRef = useRef<HTMLAudioElement | null>(null)
  const pdfViewerRef = useRef<PdfViewerHandle | null>(null)
  const youtubePracticeRef = useRef<YouTubePracticeHandle | null>(null)
  const segnoGridRef = useRef<HTMLDivElement | null>(null)
  const landscapeSegnoStripRef = useRef<HTMLDivElement | null>(null)
  const transitionRef = useRef(false)
  const timerRef = useRef<number | null>(null)
  const objectUrlRef = useRef<string | null>(null)
  const downloadAbortRef = useRef<AbortController | null>(null)
  const downloadRequestInFlightRef = useRef(false)
  // 상단 전체곡 재생과 아래 세뇨(구간) 재생을 구분하는 UI 전용 상태입니다.
  // 세뇨 재생에서 audio.play()를 호출해도 native play 이벤트가 발생하므로 ref로 즉시 구분합니다.
  const segnoPlaybackActiveRef = useRef(false)
  const generalPlaybackHintShownRef = useRef(false)
  const generalPlaybackHintTimerRef = useRef<number | null>(null)
  const wake = useWakeLock()
  const playbackVolume = usePlaybackVolume()
  // 연습 통계는 실제 media가 재생 중일 때만 시작합니다.
  // FILE은 native <audio> play/pause 이벤트, YouTube는 IFrame PlayerState를 사용합니다.
  usePracticeTelemetry(song?.shareId ?? null, Boolean(playback && (nativeAudioPlaying || youtubePlaying)))

  useEffect(() => {
    if (!song) return
    const rawTitle = album?.title ?? song.title
    const pageTitle = rawTitle?.trim()
    document.title = pageTitle ? `mioSegno - ${pageTitle}` : 'mioSegno'
    return () => {
      document.title = 'mioSegno'
    }
  }, [album?.title, song?.title])

  useEffect(() => {
    void api.status().then((status) => {
      setAppStatus(status)
      setCopyrightPolicyUrl(status.copyrightPolicyUrl ?? null)
    }).catch(() => undefined)
  }, [])

  function setDownloadedBlob(blob: Blob | null) {
    if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current)
    objectUrlRef.current = null
    if (!blob) {
      setSourceUrl(null)
      return
    }
    const localUrl = URL.createObjectURL(blob)
    objectUrlRef.current = localUrl
    setSourceUrl(localUrl)
  }

  async function downloadAndPrepareAudio(
    playUrl: string,
    cacheKey: string,
    accessExpiresAt: string,
    signal?: AbortSignal,
  ) {
    setSourceUrl(null)
    setCacheState('downloading')
    setDownloadProgress(0)
    setMessage(t('practice.audio.preparingDownload'))

    try {
      const blob = await downloadAudioFully(playUrl, (progress) => {
        if (!signal?.aborted) setDownloadProgress(progress.percent)
      }, signal)
      if (ownerPractice) {
        await audioStore.put(cacheKey, blob)
      } else {
        if (!shareId) throw new Error('SHARE_ID_MISSING')
        await audioStore.putShared(cacheKey, blob, shareId, accessExpiresAt)
      }
      if (signal?.aborted) return

      setDownloadedBlob(blob)
      setCacheState('ready')
      setDownloadProgress(100)
      setMessage('')
    } catch (error) {
      if (signal?.aborted) return
      setCacheState('error')
      setDownloadProgress(null)
      setSourceUrl(null)
      setMessage(userMessageForError(error, t('practice.error.audioDownload')))
    }
  }

  useEffect(() => {
    let cancelled = false
    const controller = new AbortController()
    downloadAbortRef.current = controller
    segnoPlaybackActiveRef.current = false
    generalPlaybackHintShownRef.current = false
    setGeneralPlaybackHintVisible(false)
    if (generalPlaybackHintTimerRef.current) {
      window.clearTimeout(generalPlaybackHintTimerRef.current)
      generalPlaybackHintTimerRef.current = null
    }

    async function load() {
      // 공유 링크가 만료·중지되었을 때 이전 곡/로컬 캐시 상태가 화면에 남아
      // 서버 검증을 우회하는 것처럼 보이지 않도록 새 검증 전에 재생 상태를 비웁니다.
      setSong(null)
      setPlayback(null)
      setDownloadedBlob(null)
      setSourceUrl(null)
      setCacheState('checking')
      setDownloadProgress(null)
      try {
        await audioStore.pruneExpiredShared().catch(() => undefined)
        const [loadedSong, loadedPlayback] = ownerPractice
          ? await Promise.all([
              api.getSong(ownerSongId!),
              api.getOwnerPlayback(ownerSongId!),
            ])
          : await Promise.all([
              api.getSharedSong(shareId!),
              api.getPlayback(shareId!),
            ])
        if (cancelled) return
        setSong(loadedSong)
        setPlayback(loadedPlayback)
        const firstOrder = firstSegnoOrder(loadedSong.segnos)
        setSelected(firstOrder == null ? new Set<number>() : new Set([firstOrder]))
        setActiveOrder(firstOrder)
        setContinuousPlayback(true)

        if (loadedPlayback.sourceType === 'FILE') {
          const cacheKey = audioCacheKey(loadedSong.mediaFileId, loadedSong.shareId)
          setCacheState('checking')
          const accessExpiresAt = loadedPlayback.accessExpiresAt
          const cached = ownerPractice
            ? await audioStore.get(cacheKey)
            : accessExpiresAt && shareId
              ? await audioStore.getShared(cacheKey, shareId, accessExpiresAt)
              : null
          if (cancelled) return
          if (cached) {
            setDownloadedBlob(cached)
            setCacheState('ready')
            setDownloadProgress(100)
            setMessage('')
          } else {
            setSourceUrl(null)
            setCacheState('waiting')
            setDownloadProgress(null)
            setMessage('')
          }
        } else {
          // 정상적인 유튜브 연결 상태는 별도 안내문을 차지하지 않습니다.
          setMessage('')
        }
      } catch (error) {
        if (controller.signal.aborted) return
        if (!ownerPractice && shareId) {
          await audioStore.removeSharedByShareId(shareId).catch(() => undefined)
        }
        const fallback = ownerPractice ? t('practice.error.loadOwner') : t('practice.error.loadShared')
        const text = userMessageForError(error, fallback)
        setMessage(text)
      }
    }
    void load()
    return () => {
      cancelled = true
      controller.abort()
      if (timerRef.current) window.clearTimeout(timerRef.current)
      if (generalPlaybackHintTimerRef.current) {
        window.clearTimeout(generalPlaybackHintTimerRef.current)
        generalPlaybackHintTimerRef.current = null
      }
      if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current)
    }
  }, [ownerPractice, ownerSongId, shareId])

  const sortedSegnos = useMemo(() => song ? orderedSegnos(song.segnos) : [], [song])
  const queue = useMemo(
    () => song ? selectedQueue(song.segnos, selected) : [],
    [selected, song],
  )
  const continuousRange = useMemo(
    () => song ? continuousSelectedRange(song.segnos, selected) : null,
    [selected, song],
  )
  const continuousMode = continuousPlayback && continuousRange !== null
  const active = sortedSegnos.find((segno) => segno.order === activeOrder) ?? null
  const activeIndex = sortedSegnos.findIndex((segno) => segno.order === activeOrder)
  const pdfPlaybackDisabled = queue.length === 0
    || (playback?.sourceType === 'FILE' && (cacheState !== 'ready' || !sourceUrl))

  useEffect(() => {
    const audio = audioRef.current
    if (audio) audio.volume = playbackVolume.volume / 100
  }, [playbackVolume.volume, sourceUrl])

  useEffect(() => {
    const audio = audioRef.current
    if (!audio) return
    audio.playbackRate = playbackRate
    if ('preservesPitch' in audio) audio.preservesPitch = true
  }, [playbackRate, sourceUrl])

  useEffect(() => {
    if (activeOrder == null) return

    // 세로/일반 화면의 세뇨 목록과 휴대폰 가로 YouTube 전용 세뇨 strip을 함께 동기화합니다.
    // 가로 strip은 사용자가 직접 좌우로 스크롤할 수 있고, 앞/뒤 이동이나 재생으로 현재 세뇨가
    // 바뀌면 해당 항목이 화면 안으로 자연스럽게 들어오도록 같은 로직을 재사용합니다.
    const grids = [segnoGridRef.current, landscapeSegnoStripRef.current].filter(
      (grid): grid is HTMLDivElement => grid != null,
    )
    if (grids.length === 0) return

    const frame = window.requestAnimationFrame(() => {
      grids.forEach((grid) => {
        const target = grid.querySelector<HTMLElement>(`[data-segno-order="${activeOrder}"]`)
        if (!target) return
        const gridRect = grid.getBoundingClientRect()
        const targetRect = target.getBoundingClientRect()
        const computed = window.getComputedStyle(grid)
        const paddingLeft = Number.parseFloat(computed.paddingLeft) || 0
        const nextLeft = Math.max(0, grid.scrollLeft + targetRect.left - gridRect.left - paddingLeft)
        grid.scrollTo({ left: nextLeft, behavior: 'smooth' })
      })
    })

    return () => window.cancelAnimationFrame(frame)
  }, [activeOrder])

  useEffect(() => {
    const audio = audioRef.current
    if (!audio || !('mediaSession' in navigator) || !song) return
    navigator.mediaSession.metadata = new MediaMetadata({
      title: song.title,
      artist: 'mioSegno · 2n4u',
      album: t('practice.mediaSession.album'),
    })
    navigator.mediaSession.setActionHandler('play', () => void audio.play())
    navigator.mediaSession.setActionHandler('pause', () => audio.pause())
    navigator.mediaSession.setActionHandler('seekbackward', () => {
      audio.currentTime = Math.max(0, audio.currentTime - 5)
    })
    navigator.mediaSession.setActionHandler('seekforward', () => {
      audio.currentTime = Math.min(audio.duration || Infinity, audio.currentTime + 5)
    })
    return () => {
      navigator.mediaSession.setActionHandler('play', null)
      navigator.mediaSession.setActionHandler('pause', null)
      navigator.mediaSession.setActionHandler('seekbackward', null)
      navigator.mediaSession.setActionHandler('seekforward', null)
    }
  }, [song, sourceUrl])

  function toggleSelected(order: number) {
    const next = new Set(selected)
    if (next.has(order)) {
      next.delete(order)
      if (activeOrder === order) {
        const nextActive = sortedSegnos.find((segno) => next.has(segno.order))
        setActiveOrder(nextActive?.order ?? null)
      }
    } else {
      next.add(order)
      setActiveOrder(order)
    }
    setSelected(next)
  }

  function moveCurrentSegno(direction: SegnoMoveDirection) {
    if (!song) return
    const target = adjacentSegno(song.segnos, activeOrder, direction)
    if (!target) return

    // 화살표 이동은 현재 선택을 정리하고 앞/뒤 세뇨 하나만 선택합니다.
    setSelected(new Set([target.order]))
    setActiveOrder(target.order)

    // 파일 음원은 부모가 직접 새 구간으로 이동합니다.
    // 유튜브는 queue 변경을 감지한 YouTubePractice가 같은 동작을 수행합니다.
    if (looping && playback?.sourceType === 'FILE') {
      const audio = audioRef.current
      if (!audio) return
      transitionRef.current = false
      if (timerRef.current) window.clearTimeout(timerRef.current)
      audio.currentTime = target.startTime
      void audio.play()
    }
  }

  async function startLoop() {
    const audio = audioRef.current
    if (!audio || queue.length === 0) return
    transitionRef.current = false
    segnoPlaybackActiveRef.current = true
    setLooping(true)

    const startTime = continuousMode && continuousRange ? continuousRange.startTime : queue[0].startTime
    const firstActive = continuousMode ? segnoAtTime(sortedSegnos, startTime) : queue[0]
    setActiveOrder(firstActive?.order ?? queue[0].order)
    audio.currentTime = startTime
    await audio.play()
    if (wake.supported && !wake.active) {
      await wake.request().catch(() => false)
    }
  }

  function stopLoop() {
    segnoPlaybackActiveRef.current = false
    setLooping(false)
    transitionRef.current = false
    if (timerRef.current) window.clearTimeout(timerRef.current)
    audioRef.current?.pause()
  }

  function togglePdfToolbarPlayback() {
    if (playback?.sourceType === 'FILE') {
      if (looping) stopLoop()
      else void startLoop()
      return
    }

    if (looping) youtubePracticeRef.current?.stop()
    else youtubePracticeRef.current?.startLoop()
  }

  function handleNativeAudioPlay() {
    // 재생 telemetry는 전체곡 재생과 세뇨 재생을 모두 실제 재생 상태에서만 집계합니다.
    setNativeAudioPlaying(true)

    // 아래 세뇨 버튼의 programmatic play에는 안내를 표시하지 않습니다.
    // 상단 브라우저 audio controls에서 사용자가 전체곡 재생을 시작했을 때만 곡당 1회 표시합니다.
    if (segnoPlaybackActiveRef.current || generalPlaybackHintShownRef.current) return

    generalPlaybackHintShownRef.current = true
    setGeneralPlaybackHintVisible(true)
    if (generalPlaybackHintTimerRef.current) window.clearTimeout(generalPlaybackHintTimerRef.current)
    generalPlaybackHintTimerRef.current = window.setTimeout(() => {
      setGeneralPlaybackHintVisible(false)
      generalPlaybackHintTimerRef.current = null
    }, GENERAL_PLAYBACK_HINT_DURATION_MS)

    if (import.meta.env.DEV) {
      console.debug('[mioSegno][PracticePlayer] native full-track playback hint shown')
    }
  }

  function advance(current: Segno) {
    const audio = audioRef.current
    if (!audio || queue.length === 0 || transitionRef.current) return
    transitionRef.current = true
    const index = Math.max(0, queue.findIndex((item) => item.order === current.order))
    const next = queue[(index + 1) % queue.length]
    setActiveOrder(next.order)

    if (isContiguous(current, next)) {
      audio.currentTime = next.startTime
      transitionRef.current = false
      void audio.play()
      return
    }

    audio.pause()
    timerRef.current = window.setTimeout(() => {
      audio.currentTime = next.startTime
      transitionRef.current = false
      void audio.play()
    }, PAUSE_MS)
  }

  function onTimeUpdate() {
    const audio = audioRef.current
    if (!looping || !audio) return

    if (continuousMode && continuousRange) {
      const atTime = segnoAtTime(sortedSegnos, audio.currentTime)
      if (atTime && atTime.order !== activeOrder) setActiveOrder(atTime.order)

      if (audio.currentTime >= continuousRange.endTime - 0.03) {
        const firstActive = segnoAtTime(sortedSegnos, continuousRange.startTime)
        setActiveOrder(firstActive?.order ?? queue[0]?.order ?? null)
        audio.currentTime = continuousRange.startTime
        void audio.play()
      }
      return
    }

    if (active && audio.currentTime >= active.endTime - 0.03) {
      advance(active)
    }
  }

  function jump(seconds: number) {
    const audio = audioRef.current
    if (!audio) return
    audio.currentTime = clampSeekTime(audio.currentTime, audio.duration, seconds)
  }

  async function downloadAudioToDevice() {
    if (!song || playback?.sourceType !== 'FILE' || downloadRequestInFlightRef.current) return
    downloadRequestInFlightRef.current = true
    downloadAbortRef.current?.abort()
    const controller = new AbortController()
    downloadAbortRef.current = controller
    // 버튼을 즉시 숨겨 빠른 더블클릭으로 다운로드 권한이 두 번 발급되는 것도 막습니다.
    setCacheState('downloading')
    setDownloadProgress(0)

    try {
      const freshPlayback = ownerPractice
        ? await api.getOwnerPlayback(ownerSongId!)
        : await api.getSharedDownload(shareId!)
      setPlayback(freshPlayback)
      if (!freshPlayback.playUrl) throw new Error('AUDIO_DOWNLOAD_URL_MISSING')
      if (!freshPlayback.accessExpiresAt) throw new Error('AUDIO_ACCESS_EXPIRY_MISSING')
      await downloadAndPrepareAudio(
        freshPlayback.playUrl,
        audioCacheKey(song.mediaFileId, song.shareId),
        freshPlayback.accessExpiresAt,
        controller.signal,
      )
    } catch (error) {
      if (controller.signal.aborted) return
      setCacheState('error')
      setDownloadProgress(null)
      setMessage(userMessageForError(error, t('practice.error.audioDownload')))
    } finally {
      downloadRequestInFlightRef.current = false
    }
  }

  function selectPracticeTab(nextTab: 'lyrics' | 'pdf') {
    setTab(nextTab)
    setPdfToolsCompact(true)
  }

  useEffect(() => {
    if (tab !== 'pdf' || pdfToolsCompact) return
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setPdfToolsCompact(true)
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [pdfToolsCompact, tab])

  useEffect(() => {
    document.documentElement.classList.toggle('practice-fullscreen-active', practiceFullscreen)
    return () => document.documentElement.classList.remove('practice-fullscreen-active')
  }, [practiceFullscreen])

  useEffect(() => {
    const syncFullscreenState = () => {
      if (document.fullscreenElement === practicePageRef.current) {
        setPracticeFullscreen(true)
        return
      }
      if (nativeFullscreenEnteredRef.current && document.fullscreenElement == null) {
        nativeFullscreenEnteredRef.current = false
        setPracticeFullscreen(false)
      }
    }
    document.addEventListener('fullscreenchange', syncFullscreenState)
    return () => document.removeEventListener('fullscreenchange', syncFullscreenState)
  }, [])

  async function togglePracticeFullscreen() {
    if (practiceFullscreen) {
      if (document.fullscreenElement) {
        try {
          await document.exitFullscreen()
        } catch {
          // 브라우저가 네이티브 전체화면 종료를 거부해도 레이아웃 확장 모드는 종료합니다.
        }
      }
      nativeFullscreenEnteredRef.current = false
      setPracticeFullscreen(false)
      return
    }

    setPracticeFullscreen(true)
    const target = practicePageRef.current
    if (!target?.requestFullscreen) {
      setMessage(t('practice.fullscreen.apiUnavailable'))
      return
    }

    try {
      await target.requestFullscreen()
      nativeFullscreenEnteredRef.current = true
    } catch {
      setMessage(t('practice.fullscreen.restricted'))
    }
  }

  async function submitReport(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault()
    if (!shareId || ownerPractice) return
    const form = new FormData(event.currentTarget)
    try {
      await api.report({
        shareId,
        reporterName: String(form.get('reporterName') ?? ''),
        reporterEmail: String(form.get('email') ?? ''),
        rightsDescription: String(form.get('rightsDescription') ?? ''),
        infringementDescription: String(form.get('infringementDescription') ?? ''),
        claimantEvidence: String(form.get('claimantEvidence') ?? ''),
        additionalNotes: String(form.get('additionalNotes') ?? ''),
      })
      setMessage(t('practice.report.submitted'))
      setReportOpen(false)
      stopLoop()
    } catch (error) {
      setMessage(userMessageForError(error, t('practice.error.report')))
    }
  }

  if (!song || !playback) {
    return (
      <main className="page">
        <button className="button ghost" onClick={onBack}>← {t('common.home')}</button>
        <section className="card empty-state">{message}</section>
      </main>
    )
  }

  // Shared-file recipients arrive to practice, not to learn mioSegno.
  // Keep the required download and the first practice action in one clear,
  // state-driven guide while leaving the owner's existing workflow untouched.
  const sharedFilePractice = !ownerPractice && playback.sourceType === 'FILE'

  return (
    <main ref={practicePageRef} className={`page practice-page ${tab === 'pdf' ? 'pdf-practice-mode' : ''} ${pdfToolsCompact ? 'pdf-tools-compact' : ''} ${practiceFullscreen ? 'practice-fullscreen-mode' : ''}`}>
      <header className="practice-header compact-practice-header">
        <div className="practice-header-left-actions">
          <button
            type="button"
            className="practice-home-button"
            title={t('practice.action.home')}
            aria-label={t('practice.action.home')}
            onClick={onBack}
          >
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path d="m3 11 9-7 9 7" />
              <path d="M5.5 10v10h13V10M9 20v-6h6v6" />
            </svg>
          </button>
          {album && onAlbumSongSelect && (
            <button
              type="button"
              className="practice-home-button practice-album-button"
              title={t('practice.album.selectSong')}
              aria-label={t('practice.album.selectSong')}
              aria-expanded={albumPickerOpen}
              onClick={() => setAlbumPickerOpen((current) => !current)}
            >
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <path d="M5 5h14v4H5zM5 11h14v8H5z" />
                <path d="M8 14h8M8 17h5" />
              </svg>
            </button>
          )}
          <button
            type="button"
            className={practiceFullscreen ? 'practice-home-button practice-fullscreen-button active' : 'practice-home-button practice-fullscreen-button'}
            title={practiceFullscreen ? t('practice.fullscreen.exit') : t('practice.fullscreen.enter')}
            aria-label={practiceFullscreen ? t('practice.fullscreen.exit') : t('practice.fullscreen.enter')}
            aria-pressed={practiceFullscreen}
            onClick={() => void togglePracticeFullscreen()}
          >
            {practiceFullscreen ? (
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <path d="M9 4v5H4M15 4v5h5M9 20v-5H4M15 20v-5h5" />
              </svg>
            ) : (
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <path d="M4 9V4h5M20 9V4h-5M4 15v5h5M20 15v5h-5" />
              </svg>
            )}
          </button>
        </div>
        <div className="practice-title-block"><h1 title={song.title}>{song.title}</h1>{ownerPractice ? <small className="muted">{t('practice.period.retention')}: {formatPracticeAvailableDate(song.expiresAt, locale)}</small> : song.shareActive && song.shareExpiresAt ? <small className="muted">{t('practice.period.available')}: {formatPracticeAvailableDate(song.shareExpiresAt, locale)}</small> : null}</div>
        <PracticePreferenceControls
          wakeSupported={wake.supported}
          wakeEnabled={wake.enabled}
          wakeActive={wake.active}
          onWakeEnable={wake.enable}
          onWakeDisable={wake.disable}
          volume={playbackVolume.volume}
          muted={playbackVolume.muted}
          onVolumeChange={playbackVolume.setVolume}
          onToggleMute={playbackVolume.toggleMute}
        />
      </header>

      {album && onAlbumSongSelect && albumPickerOpen && (
        <>
          <button type="button" className="album-picker-backdrop" aria-label={t('practice.album.closePicker')} onClick={() => setAlbumPickerOpen(false)} />
          <section className="card album-song-picker" role="dialog" aria-modal="true" aria-label={t('practice.album.dialogLabel', { title: album.title })}>
            <div className="section-title-row">
              <div>
                <p className="eyebrow">{t('practice.album.eyebrow')}</p>
                <h2>{album.title}</h2>
              </div>
              <button type="button" className="button ghost" onClick={() => setAlbumPickerOpen(false)}>{t('common.close')}</button>
            </div>
            <div className="album-practice-song-list">
              {album.songs.map((albumSong) => (
                <button
                  type="button"
                  key={albumSong.shareId}
                  className={albumSong.shareId === shareId ? 'album-practice-song active' : 'album-practice-song'}
                  disabled={!albumSong.available}
                  onClick={() => {
                    onAlbumSongSelect(albumSong.shareId)
                    setAlbumPickerOpen(false)
                  }}
                >
                  <span>{albumSong.order}</span>
                  <strong>{albumSong.title}</strong>
                  <small>{albumSong.available ? (albumSong.sourceType === 'YOUTUBE' ? t('practice.source.youtube') : t('practice.source.audio')) : t('practice.album.unavailable')}</small>
                </button>
              ))}
            </div>
          </section>
        </>
      )}

      {message && <p className="notice practice-message">{message}</p>}
      {generalPlaybackHintVisible && (
        <div className="practice-playback-hint" role="status" aria-live="polite">
          {t('practice.generalPlaybackHint')}
        </div>
      )}

      {tab === 'pdf' && (
        <button
          type="button"
          className="pdf-tools-backdrop"
          aria-label={t('practice.tools.close')}
          tabIndex={pdfToolsCompact ? -1 : 0}
          onClick={() => setPdfToolsCompact(true)}
        />
      )}

      <div
        className={`practice-tools-column ${tab === 'pdf' ? 'pdf-tools-overlay-drawer' : ''}`}
        role={tab === 'pdf' ? 'dialog' : undefined}
        aria-modal={tab === 'pdf' && !pdfToolsCompact ? true : undefined}
        aria-hidden={tab === 'pdf' && pdfToolsCompact ? true : undefined}
        aria-label={tab === 'pdf' ? t('practice.tools.title') : undefined}
      >
      {tab === 'pdf' && (
        <>
          <div className="pdf-tools-drawer-heading">
            <div className="pdf-tools-drawer-title">
              <span className="pdf-tools-drawer-handle" aria-hidden="true" />
              <strong>{t('practice.tools.title')}</strong>
            </div>
            <button
              type="button"
              className="button ghost"
              onClick={() => setPdfToolsCompact(true)}
            >
              {t('practice.tools.scoreView')}
            </button>
          </div>
        </>
      )}
      {playback.sourceType === 'FILE' ? (
        <section className={`card sticky-player ${sharedFilePractice ? 'shared-practice-player' : ''}`}>
          {sharedFilePractice && (
            <div className={`shared-practice-guide shared-practice-guide-state-${cacheState}`} aria-label={t('practice.sharedGuide.badge')}>
              <span className="shared-practice-guide-badge">{t('practice.sharedGuide.badge')}</span>
              {cacheState === 'checking' && (
                <>
                  <h2>{t('practice.sharedGuide.checkingTitle')}</h2>
                  <p>{t('practice.sharedGuide.checkingDescription')}</p>
                </>
              )}
              {(cacheState === 'waiting' || cacheState === 'error') && (
                <>
                  <span className="shared-practice-guide-step">1</span>
                  <h2>{cacheState === 'error' ? t('practice.sharedGuide.retryTitle') : t('practice.sharedGuide.downloadTitle')}</h2>
                  <p>{t('practice.sharedGuide.downloadDescription')}</p>
                  <button type="button" className="button primary shared-practice-primary-action" onClick={() => void downloadAudioToDevice()}>
                    {cacheState === 'error' ? t('practice.audio.redownload') : t('practice.sharedGuide.downloadButton')}
                  </button>
                </>
              )}
              {cacheState === 'downloading' && (
                <>
                  <span className="shared-practice-guide-step">1</span>
                  <h2>{t('practice.sharedGuide.downloadingTitle')}</h2>
                  <p>{t('practice.sharedGuide.downloadingDescription')}</p>
                  <div className="audio-download-status" role="status" aria-live="polite">
                    <progress max={100} value={downloadProgress ?? undefined} />
                    <span>{downloadProgress == null ? t('practice.audio.downloading') : t('practice.audio.downloadingProgress', { percent: downloadProgress })}</span>
                  </div>
                </>
              )}
              {cacheState === 'ready' && sourceUrl && (
                <div className="shared-practice-ready-row">
                  <span className="shared-practice-guide-ready">✓ {t('practice.sharedGuide.readyBadge')}</span>
                  <button
                    type="button"
                    className={looping
                      ? 'button primary shared-practice-primary-action shared-practice-ready-action active'
                      : 'button primary shared-practice-primary-action shared-practice-ready-action'}
                    disabled={queue.length === 0}
                    title={looping ? t('practice.sharedGuide.stopButton') : t('practice.sharedGuide.startButton')}
                    aria-label={looping ? t('practice.sharedGuide.stopButton') : t('practice.sharedGuide.startButton')}
                    aria-pressed={looping}
                    onClick={() => looping ? stopLoop() : void startLoop()}
                  >
                    <span aria-hidden="true">{looping ? '■' : '▶'}</span>
                    <strong>{active?.title ?? t('practice.sharedGuide.selectSegmentPrompt')}</strong>
                  </button>
                </div>
              )}
            </div>
          )}

          {cacheState === 'ready' && sourceUrl ? (
            sharedFilePractice ? (
              <>
                <span className="shared-practice-full-song-label">{t('practice.sharedGuide.fullSongHint')}</span>
                <div className="shared-practice-full-song-row">
                  <audio
                    ref={audioRef}
                    src={sourceUrl}
                    controls
                    preload="auto"
                    onPlay={handleNativeAudioPlay}
                    onPause={() => setNativeAudioPlaying(false)}
                    onEnded={() => setNativeAudioPlaying(false)}
                    onTimeUpdate={onTimeUpdate}
                  />
                  <div className="shared-practice-speed-inline">
                    <PlaybackSpeedControl
                      value={playbackRate}
                      onChange={setPlaybackRate}
                      disabled={false}
                    />
                  </div>
                </div>
              </>
            ) : (
              <>
                <audio
                  ref={audioRef}
                  src={sourceUrl}
                  controls
                  preload="auto"
                  onPlay={handleNativeAudioPlay}
                  onPause={() => setNativeAudioPlaying(false)}
                  onEnded={() => setNativeAudioPlaying(false)}
                  onTimeUpdate={onTimeUpdate}
                />
                <div className="practice-media-control-strip">
                  <PlaybackSeekControls
                    onSeek={jump}
                    onPlay={startLoop}
                    onStop={stopLoop}
                    disabled={cacheState !== 'ready' || !sourceUrl}
                    playDisabled={cacheState !== 'ready' || !sourceUrl || queue.length === 0}
                    playing={looping}
                    playLabel={t('practice.action.startLoop')}
                    stopLabel={t('practice.action.stopLoop')}
                    className="practice-speed-seek-controls"
                  />
                  <PlaybackSpeedControl
                    value={playbackRate}
                    onChange={setPlaybackRate}
                    disabled={cacheState !== 'ready' || !sourceUrl}
                  />
                </div>
              </>
            )
          ) : !sharedFilePractice ? (
            <div className="audio-player-placeholder" aria-hidden="true">{t('practice.audio.availableAfterDownload')}</div>
          ) : null}

          {!sharedFilePractice && cacheState === 'checking' && (
            <p className="muted audio-download-status">{t('practice.audio.checkingDevice')}</p>
          )}
          {!sharedFilePractice && cacheState === 'waiting' && (
            <div className="audio-download-required notice warning" role="status">
              <strong>{t('practice.audio.noStreamingTitle')}</strong>
              <span>{t('practice.audio.noStreamingDescription')}</span>
              <button type="button" className="button primary" onClick={() => void downloadAudioToDevice()}>{t('practice.audio.download')}</button>
            </div>
          )}
          {!sharedFilePractice && cacheState === 'downloading' && (
            <div className="audio-download-status" role="status" aria-live="polite">
              <progress max={100} value={downloadProgress ?? undefined} />
              <span>{downloadProgress == null ? t('practice.audio.downloading') : t('practice.audio.downloadingProgress', { percent: downloadProgress })}</span>
            </div>
          )}
          {!sharedFilePractice && cacheState === 'error' && (
            <div className="control-row playback-extra-controls">
              <button className="button secondary" onClick={() => void downloadAudioToDevice()}>{t('practice.audio.redownload')}</button>
            </div>
          )}
        </section>
      ) : playback.youtubeUrl ? (
        <YouTubePractice
          ref={youtubePracticeRef}
          youtubeUrl={playback.youtubeUrl}
          title={song.title}
          queue={queue}
          segnos={sortedSegnos}
          continuousRange={continuousMode ? continuousRange : null}
          activeOrder={activeOrder}
          onActiveOrder={setActiveOrder}
          requestWakeLock={wake.request}
          onLoopingChange={setLooping}
          onPlayingChange={setYoutubePlaying}
          volume={playbackVolume.volume}
          compactPortrait={!ownerPractice}
        />
      ) : (
        <section className="card error-state">{t('practice.youtube.invalidUrl')}</section>
      )}

      {playback.sourceType === 'YOUTUBE' && playback.youtubeUrl ? (
        <>
          <button
            type="button"
            className={practiceFullscreen ? 'youtube-landscape-video-fullscreen-button active' : 'youtube-landscape-video-fullscreen-button'}
            title={practiceFullscreen ? t('practice.fullscreen.exit') : t('practice.fullscreen.enter')}
            aria-label={practiceFullscreen ? t('practice.fullscreen.exit') : t('practice.fullscreen.enter')}
            aria-pressed={practiceFullscreen}
            onClick={() => void togglePracticeFullscreen()}
          >
            {practiceFullscreen ? (
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <path d="M9 4v5H4M15 4v5h5M9 20v-5H4M15 20v-5h5" />
              </svg>
            ) : (
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <path d="M4 9V4h5M20 9V4h-5M4 15v5h5M20 15v5h-5" />
              </svg>
            )}
          </button>
          <div className="youtube-landscape-bottom-row">
            {/*
              Keep the landscape advertisement column structurally present even
              when the ad policy/provider renders no creative. The bottom bar is
              intentionally split into two stable halves: ad on the left and
              essential practice controls on the right.
            */}
            <div className="youtube-landscape-ad-pane">
              <NonPersonalizedAdSlot
                status={appStatus}
                eligible={Boolean(playback.showAds)}
                placement={ownerPractice ? "owner-segno-list" : "shared-practice-segno-list"}
                compactBanner
              />
            </div>
            <div className="youtube-landscape-essential-controls" aria-label={t('practice.youtube.landscapeControls')}>
              <button
                type="button"
                className="youtube-landscape-control-button youtube-landscape-previous-button"
                disabled={activeIndex <= 0}
                title={t('practice.segno.previous')}
                aria-label={t('practice.segno.previous')}
                onClick={() => moveCurrentSegno(-1)}
              >
                <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M15.5 4.5 8 12l7.5 7.5" /></svg>
              </button>
              <button
                type="button"
                className={looping ? 'youtube-landscape-control-button youtube-landscape-play-button active' : 'youtube-landscape-control-button youtube-landscape-play-button'}
                disabled={queue.length === 0}
                title={looping ? t('practice.segno.stop') : t('practice.segno.playSelected')}
                aria-label={looping ? t('practice.segno.stop') : t('practice.segno.playSelected')}
                aria-pressed={looping}
                onClick={togglePdfToolbarPlayback}
              >
                {looping ? (
                  <svg viewBox="0 0 24 24" aria-hidden="true" className="youtube-landscape-stop-icon"><rect x="6" y="6" width="12" height="12" rx="1.2" /></svg>
                ) : (
                  <svg viewBox="0 0 24 24" aria-hidden="true" className="youtube-landscape-play-icon"><path d="m8 5 11 7-11 7Z" /></svg>
                )}
              </button>
              <div
                ref={landscapeSegnoStripRef}
                className="youtube-landscape-segno-strip"
                role="group"
                aria-label={t('practice.segno.choose')}
              >
                {sortedSegnos.map((segno) => (
                  <label
                    key={segno.order}
                    data-segno-order={segno.order}
                    className={`youtube-landscape-segno-chip ${activeOrder === segno.order ? 'active' : ''}`}
                    title={segno.title}
                  >
                    <input
                      type="checkbox"
                      checked={selected.has(segno.order)}
                      onChange={() => toggleSelected(segno.order)}
                    />
                    <span>{segno.title}</span>
                  </label>
                ))}
              </div>
              <button
                type="button"
                className="youtube-landscape-control-button youtube-landscape-next-button"
                disabled={activeIndex < 0 || activeIndex >= sortedSegnos.length - 1}
                title={t('practice.segno.next')}
                aria-label={t('practice.segno.next')}
                onClick={() => moveCurrentSegno(1)}
              >
                <svg viewBox="0 0 24 24" aria-hidden="true"><path d="m8.5 4.5 7.5 7.5-7.5 7.5" /></svg>
              </button>
            </div>
          </div>
        </>
      ) : (
        <NonPersonalizedAdSlot
          status={appStatus}
          eligible={Boolean(playback.showAds)}
          placement={ownerPractice ? "owner-segno-list" : "shared-practice-segno-list"}
        />
      )}

      <section className="card segno-list-card">
        <div className={`segno-list-heading ${!ownerPractice ? 'shared-segno-list-heading' : ''}`}>
          <button
            type="button"
            className={!ownerPractice ? 'button ghost segno-navigation-button shared-segno-navigation-button' : 'button ghost segno-navigation-button'}
            disabled={activeIndex <= 0}
            title={t('practice.segno.selectPrevious')}
            aria-label={t('practice.segno.selectPrevious')}
            onClick={() => moveCurrentSegno(-1)}
          >
            <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M15.5 4.5 8 12l7.5 7.5" /></svg>
            {!ownerPractice && <span>{t('practice.sharedGuide.previousSegment')}</span>}
          </button>
          {!ownerPractice ? (
            <div className="shared-segno-heading-center">
              <small className="muted shared-segno-kicker">{t('practice.sharedGuide.segmentListTitle')}</small>
              <h2>{active?.title ?? t('practice.sharedGuide.selectSegmentPrompt')}</h2>
            </div>
          ) : (
            <div>
              <h2>{t('practice.segno.list')}</h2>
            </div>
          )}
          <button
            type="button"
            className={!ownerPractice ? 'button ghost segno-navigation-button shared-segno-navigation-button' : 'button ghost segno-navigation-button'}
            disabled={activeIndex < 0 || activeIndex >= sortedSegnos.length - 1}
            title={t('practice.segno.selectNext')}
            aria-label={t('practice.segno.selectNext')}
            onClick={() => moveCurrentSegno(1)}
          >
            {!ownerPractice && <span>{t('practice.sharedGuide.nextSegment')}</span>}
            <svg viewBox="0 0 24 24" aria-hidden="true"><path d="m8.5 4.5 7.5 7.5-7.5 7.5" /></svg>
          </button>
        </div>
        {(ownerPractice || sortedSegnos.length > 1) && (
          <div ref={segnoGridRef} className="segno-grid" aria-label={t('practice.segno.selectionList')}>
            {sortedSegnos.map((segno) => (
              <label
                key={segno.order}
                data-segno-order={segno.order}
                className={`segno-choice ${activeOrder === segno.order ? 'active' : ''}`}
              >
                <input
                  type="checkbox"
                  checked={selected.has(segno.order)}
                  onChange={() => toggleSelected(segno.order)}
                />
                <span>
                  <strong>{segno.title}</strong>
                </span>
              </label>
            ))}
          </div>
        )}
        {(!sharedFilePractice || sortedSegnos.length > 1) && (
          <label className={queue.length > 1 ? 'continuous-playback-option' : 'continuous-playback-option disabled'}>
            <input
              type="checkbox"
              checked={continuousPlayback}
              disabled={queue.length < 2}
              onChange={(event) => setContinuousPlayback(event.target.checked)}
            />
            <span>{sharedFilePractice ? t('practice.sharedGuide.continuousShort') : t('practice.segno.continuous')}</span>
          </label>
        )}
      </section>
      </div>

      <section className="viewer-tabs practice-viewer-column">
        <div className="tab-row">
          <button className={tab === 'lyrics' ? 'tab active' : 'tab'} onClick={() => selectPracticeTab('lyrics')}>
            {t('practice.tab.lyrics')}
          </button>
          <button className={tab === 'pdf' ? 'tab active' : 'tab'} onClick={() => selectPracticeTab('pdf')}>
            {t('practice.tab.pdf')}
          </button>
          {tab === 'pdf' && (
            <div className="pdf-tab-actions">
              <button
                type="button"
                className="button secondary pdf-open-source-button"
                onClick={() => pdfViewerRef.current?.openSourceDialog()}
              >
                {pdfHasDocument ? t('practice.pdf.change') : t('practice.pdf.open')}
              </button>
              <button
                type="button"
                className="button ghost pdf-tools-toggle"
                aria-expanded={!pdfToolsCompact}
                onClick={() => setPdfToolsCompact((current) => !current)}
              >
                {pdfToolsCompact ? t('practice.tools.show') : t('practice.tools.scoreView')}
              </button>
            </div>
          )}
        </div>
        {tab === 'lyrics' ? (
          <article className="card lyrics-viewer">
            <p className="eyebrow">{active?.title ?? t('practice.sharedGuide.selectSegmentPrompt')}</p>
            <p>{active?.lyrics || t('practice.lyrics.empty')}</p>
          </article>
        ) : (
          <div className="card pdf-viewer-card">
            <PdfViewer
              ref={pdfViewerRef}
              shareId={song.shareId}
              segnos={sortedSegnos}
              activeOrder={activeOrder}
              onDocumentStateChange={setPdfHasDocument}
              onPreviousSegno={() => moveCurrentSegno(-1)}
              onNextSegno={() => moveCurrentSegno(1)}
              previousSegnoDisabled={activeIndex <= 0}
              nextSegnoDisabled={activeIndex < 0 || activeIndex >= sortedSegnos.length - 1}
              playing={looping}
              playbackDisabled={pdfPlaybackDisabled}
              onTogglePlayback={togglePdfToolbarPlayback}
            />
          </div>
        )}
      </section>

      {!ownerPractice && shareId && <footer className="practice-footer">
        <button className="button danger ghost" onClick={() => setReportOpen((value) => !value)}>
          {t('practice.report.open')}
        </button>
        {reportOpen && (
          <form className="card report-form" onSubmit={(event) => void submitReport(event)}>
            <p className="muted">{t('practice.report.descriptionPrefix')} <strong>{shareId}</strong>{t('practice.report.descriptionSuffix')}</p>
            <label>{t('practice.report.reporter')}<input name="reporterName" required maxLength={200} /></label>
            <label>{t('practice.report.email')}<input name="email" type="email" required maxLength={320} /></label>
            <label>{t('practice.report.rightsDescription')}<textarea name="rightsDescription" required maxLength={2000} /></label>
            <label>{t('practice.report.infringement')}<textarea name="infringementDescription" required maxLength={4000} /></label>
            <label>{t('practice.report.evidence')}<textarea name="claimantEvidence" required maxLength={10000} /></label>
            <label>{t('practice.report.additionalNotes')}<textarea name="additionalNotes" maxLength={10000} /></label>
            {copyrightPolicyUrl && <a href={copyrightPolicyUrl} target="_blank" rel="noreferrer">{t('practice.report.policy')}</a>}
            <button className="button danger" type="submit">{t('practice.report.submit')}</button>
          </form>
        )}
      </footer>}
    </main>
  )
}
